import { getSubUrlName } from '@sunbit-dev/common/helpers/url';
import IHttpClient from '@sunbit-dev/common/services/httpClient/IHttpClient';
import DocumentTypes from '@sunbit-dev/common/enums/DocumentTypes';
import IDocument from '@sunbit-dev/common/models/IDocument';
import IKeyValue from '@sunbit-dev/common/models/IKeyValue';
import ICustomer from '@sunbit-dev/common/models/ICustomer';
import IOffer from '@sunbit-dev/common/models/IOffer';
import { IDebitCardDetailsMandatory } from '@sunbit-dev/common/models/IDebitCardDetails';
import { ITestData } from '~/contexts/abTests/models';

// TODO: from epay, move to common
interface IOfferRequest {
  timeZone?: string;
  customer?: {
    ssn: string;
  };
}

class SunbitApi {
  private httpClient!: IHttpClient;
  private subUrlName!: string;
  private userToken!: string;

  public init(httpClient: IHttpClient) {
    this.httpClient = httpClient;
    this.subUrlName = getSubUrlName(window.location);
    return this;
  }

  public getDocument(documentType: DocumentTypes, retailerId: number) {
    const config: { params: IKeyValue } = {
      params: {},
    };
    if (retailerId) {
      config.params.retailerId = retailerId;
    }
    return this.httpClient.get(`/api/v1/documents/generate/${documentType}`, config).then((response: any): IDocument => {
      return response.data;
    });
  }

  public setStoreToken(userToken: string) {
    this.userToken = userToken;
    this.setLoginWithToken(this.userToken);
    return this;
  }

  private setLoginWithToken(token: string): boolean {
    this.httpClient.setHeader('Authorization', `Bearer ${token}`);
    return true;
  }

  public async getRetailerData() {
    return this.httpClient.get(`/api/v1/epay/pre-qual/${this.subUrlName}`).then((response) => {
      return response.data;
    });
  }

  private async login(phoneNumber: string, verificationCode: string | null, pincodeMethod: string, departmentId: number, reCaptchaToken?: string) {
    // Translate pincode method to boolean required by the api
    const automatedCall = pincodeMethod === 'call' ? true : false;
    const mandatorySmsConsent = pincodeMethod !== 'call' ? true : null;
    const loginData =
      departmentId === 0
        ? { phoneNumber, verificationCode, automatedCall, mandatorySmsConsent }
        : { phoneNumber, verificationCode, automatedCall, departmentId, mandatorySmsConsent };
    if (reCaptchaToken) {
      this.httpClient.setHeader('reCaptchaToken', reCaptchaToken);
    }

    return this.httpClient.post(`/api/v1/epay/pre-qual/${this.subUrlName}/login`, loginData).then((response) => {
      return response.data;
    });
  }

  public async loginVerified(): Promise<{ customer: ICustomer }> {
    return this.httpClient.post(`api/v1/epay/email-verification`, {}).then((response) => {
      return response.data;
    });
  }

  public async loginMerged(): Promise<{ customer: ICustomer }> {
    return this.httpClient.post(`api/v1/epay/email-verification/customer-merge`, {}).then((response) => {
      return response.data;
    });
  }

  public sendVerificationCode(phoneNumber: string, verificationCode: string, pincodeMethod: string, departmentId: number, reCaptchaToken: string) {
    let verification: string | null = verificationCode;
    if (verificationCode === '') {
      verification = null;
    }
    return this.login(phoneNumber, verification, pincodeMethod, departmentId, reCaptchaToken);
  }

  public createCheckoutCustomer(customer: ICustomer, skipLandingPageProperties: ITestData): Promise<ICustomer> {
    return this.httpClient.post(`/api/v1/epay/customers`, customer, { params: skipLandingPageProperties }).then((response: any): ICustomer => {
      return response.data;
    });
  }

  public checkEmailVerification() {
    return this.httpClient.get(`/api/v1/epay/customer/verified`).then((response: any): string => {
      return response.data;
    });
  }

  public resendVerificationEmail(email: string = '') {
    if (email !== '') {
      this.httpClient.setHeader('email', `${email}`);
    }
    return this.httpClient.post(`/api/v1/epay/customer/updateEmail`, {});
  }

  public updateCheckoutPurchaseAmount(purchaseId: number, amount: number): Promise<IOffer> {
    const stringAmount = amount.toString();

    return this.httpClient
      .put(`api/v1/epay/${purchaseId}/amount`, { amount: stringAmount })
      .then((response: any): IOffer => {
        //Removing downPayment from paymentPlans payments
        response.data.paymentPlans.forEach((_plan, planIndex) => {
          response.data.paymentPlans[planIndex].payments.forEach((payment, paymentIndex) => {
            if (payment.hasOwnProperty('type') && payment.type === 'downPayment') {
              response.data.paymentPlans[planIndex].payments.splice(paymentIndex, 1);
            }
          });
        });
        return response.data;
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public changeDownPayment(totalAmount: number, additionalDownPayment: string, totalDownPayment: number, offerId: number) {
    return this.httpClient
      .put(`/api/v1/epay/${offerId}?totalDownPayment=${totalDownPayment}`, {
        purchaseAdditionalDownPayments: [{ amount: additionalDownPayment, paymentMethod: 'DEBIT_CARD' }],
        totalAmount,
      })
      .then((response: any): IOffer => {
        // Removing downPayment from paymentPlans payments
        response.data.paymentPlans.forEach((_plan, planIndex) => {
          response.data.paymentPlans[planIndex].payments.forEach((payment, paymentIndex) => {
            if (payment.hasOwnProperty('type') && payment.type === 'downPayment') {
              response.data.paymentPlans[planIndex].payments.splice(paymentIndex, 1);
            }
          });
        });
        return response.data;
      });
  }

  public createCheckoutPurchase(ssn?: string): Promise<IOffer> {
    const offerRequest: IOfferRequest = {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (ssn) {
      offerRequest.customer = {
        ssn,
      };
    }

    return this.httpClient
      .post(`/api/v1/epay`, offerRequest)
      .then((response: any): IOffer => {
        //Removing downPayment from paymentPlans payments
        return response.data;
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public async createAcceptPurchasePaymentPlan(purchaseId: number, paymentPlanId: number) {
    return this.httpClient.put(`api/v1/epay/${purchaseId}/payment-plan/${paymentPlanId}`);
  }

  public async validateDebitCard(purchaseId: number, debitCardDetails: IDebitCardDetailsMandatory) {
    return await this.httpClient.put(`api/v1/epay/${purchaseId}/offers/debitCard`, debitCardDetails);
  }

  public async abortApplication(offerId: number) {
    return await this.httpClient.put(`api/v1/epay/abort/${offerId}`);
  }
}

export default new SunbitApi();
